import type { MetricCategory } from '@mfw/common';

import { MetricSampleKind, MetricServedBy, MetricSource } from '@mfw/common';
import React from 'react';

import type { ApiMetricDefinition } from '../../../api/types/stats.types';

import { useMetricDefinitions } from './useMetricDefinitions';

/**
 * Metric registry with helpers — SOURCE OF TRUTH for rendering (chartTypes,
 * aggregations, servedBy). `pickable` = metrics offered in pickers
 * (excludes `finance` — those are served by the existing finance-stats layer).
 */
export function useMetricRegistry({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const { data, ...rest } = useMetricDefinitions({ enabled });

  const helpers = React.useMemo(() => {
    const metrics = data ?? [];
    const map = new Map(metrics.map((m) => [m.key, m]));
    return {
      byCategory: (category: MetricCategory) =>
        metrics.filter((m) => m.category === category),
      byKey: (key: string): ApiMetricDefinition | undefined => map.get(key),
      dailyManualKeys: metrics.filter(
        (m) =>
          m.sampleKind === MetricSampleKind.DAILY &&
          m.allowedSources.includes(MetricSource.MANUAL) &&
          m.servedBy === MetricServedBy.SAMPLES,
      ),
      fsInputs: metrics.filter((m) => m.isFsInput),
      metrics,
      pickable: metrics.filter((m) => m.servedBy !== MetricServedBy.FINANCE),
    };
  }, [data]);

  return { ...rest, data, ...helpers };
}
